import { getSelectedPermissionCount } from "./permissionCatalogue";
import { StaffUserFormValue } from "./types";

export type StaffUserFormErrors = Partial<
  Record<"firstName" | "lastName" | "jobTitle" | "email" | "permissions", string>
>;

const SIMPLE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normalizeStaffUserFormValue = (
  value: StaffUserFormValue
): StaffUserFormValue => ({
  firstName: value.firstName.trim(),
  lastName: value.lastName.trim(),
  jobTitle: value.jobTitle.trim(),
  email: value.email.trim().toLowerCase(),
  permissions: {
    chamberMemberPro: [...value.permissions.chamberMemberPro],
    chamberPerksAppAdminConsole: [
      ...value.permissions.chamberPerksAppAdminConsole,
    ],
  },
});

const haveSameValues = (
  leftValues: readonly string[],
  rightValues: readonly string[]
): boolean =>
  leftValues.length === rightValues.length &&
  leftValues.every((value) => rightValues.includes(value));

export const isStaffUserFormDirty = ({
  currentValue,
  initialValue,
}: {
  currentValue: StaffUserFormValue;
  initialValue: StaffUserFormValue;
}): boolean => {
  const current = normalizeStaffUserFormValue(currentValue);
  const initial = normalizeStaffUserFormValue(initialValue);

  return (
    current.firstName !== initial.firstName ||
    current.lastName !== initial.lastName ||
    current.jobTitle !== initial.jobTitle ||
    current.email !== initial.email ||
    !haveSameValues(
      current.permissions.chamberMemberPro,
      initial.permissions.chamberMemberPro
    ) ||
    !haveSameValues(
      current.permissions.chamberPerksAppAdminConsole,
      initial.permissions.chamberPerksAppAdminConsole
    )
  );
};

export const validateStaffUserForm = (
  value: StaffUserFormValue
): StaffUserFormErrors => {
  const normalized = normalizeStaffUserFormValue(value);
  const errors: StaffUserFormErrors = {};

  if (!normalized.firstName) {
    errors.firstName = "Enter the user's first name.";
  } else if (normalized.firstName.length > 80) {
    errors.firstName = "First name must be 80 characters or fewer.";
  }

  if (!normalized.lastName) {
    errors.lastName = "Enter the user's last name.";
  } else if (normalized.lastName.length > 80) {
    errors.lastName = "Last name must be 80 characters or fewer.";
  }

  if (normalized.jobTitle.length > 100) {
    errors.jobTitle = "Job title must be 100 characters or fewer.";
  }

  if (!normalized.email) {
    errors.email = "Enter the user's email address.";
  } else if (
    normalized.email.length > 320 ||
    !SIMPLE_EMAIL_PATTERN.test(normalized.email)
  ) {
    errors.email = "Enter a full email address, for example name@yourchamber.ca.";
  }

  if (getSelectedPermissionCount(normalized.permissions) === 0) {
    errors.permissions = "Select at least one permission.";
  }

  return errors;
};
